<template>
  <div>
    <div
      class="drawer-background"
      :class="{ show: active }"
      @click="closeProductOrder"
    />
    <div class="drawer" :class="{ show: active }">
      <ion-header>
        <ion-toolbar>
          <ion-buttons slot="start">
            <ion-button fill="clear" @click="closeProductOrder">
              <ion-icon slot="icon-only" :icon="chevronBack"></ion-icon>
            </ion-button>
          </ion-buttons>
          <ion-title>{{ cafeInfo?.shopName || "Cafe Menu" }}</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content>
        <div v-if="product" class="product-details">
          <h1 class="product-name-centered">{{ product.name }}</h1>

          <div class="quantity-selector">
            <button
              @click="decrement()"
              class="qty-btn qty-btn-minus"
              :disabled="quantity <= 1"
            >
              <span class="qty-icon">&minus;</span>
            </button>
            <div class="qty-display">
              <span class="qty-value">{{ quantity }}</span>
            </div>
            <button @click="increment()" class="qty-btn qty-btn-plus">
              <span class="qty-icon">+</span>
            </button>
          </div>

          <!-- Dynamic variations rendering -->
          <div v-if="hasVariations">
            <template v-for="type in variationOrder">
              <div
                :key="type"
                v-if="
                  variations[type] &&
                  Array.isArray(variations[type]) &&
                  variations[type].length > 0
                "
              >
                <h2 class="product-addon">
                  {{ getVariationLabel(type) }}
                </h2>
                <div class="variations-grid">
                  <button
                    v-for="option in variations[type]"
                    :key="option.name"
                    class="variation-label"
                    :class="{
                      selected: selectedVariations[type]?.name === option.name,
                    }"
                    @click="selectVariation(type, option, $event)"
                  >
                    {{ option.name }}
                    <span
                      class="addon-price-label"
                      v-if="parseFloat(option.price) > 0"
                    >
                      +$ {{ parseFloat(option.price).toFixed(2) }}
                    </span>
                  </button>
                </div>
              </div>
            </template>
          </div>
          <div
            style="padding: 1em; font-family: 'Gilroy'; font-size: 14px"
            v-else
          >
            No product variation/s available for this item.
          </div>

          <div>
            <hr class="hrdivider" />
            <h2 class="product-addon">Special instructions</h2>
            <ion-textarea
              class="product-txtarea"
              placeholder="Add a note to this item"
              :auto-grow="true"
              v-model="instructions"
              maxlength="100"
            />
            <hr class="hrdivider" />
          </div>
        </div>
      </ion-content>
      <ion-footer>
        <ion-toolbar>
          <ion-button
            expand="block"
            color="primary"
            :class="[{ show: active }]"
            @click="addToCart"
            :disabled="isAddToOrderDisabled"
          >
            <span class="carttext-btn">{{
              isCafeOpen ? "Add to order" : "Cafe Closed"
            }}</span>
            <span class="cartprice-btn"> $ {{ totalCartPrice }} </span>
          </ion-button>
        </ion-toolbar>
      </ion-footer>
    </div>
  </div>
</template>
<script>
import { defineComponent } from "vue";
import axios from "axios";
import {
  IonButton,
  IonTextarea,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonTitle,
  IonIcon,
  IonContent,
  IonFooter,
  toastController,
} from "@ionic/vue";
import { chevronBack } from "ionicons/icons";

const BASE_URI =
  process.env.VUE_APP_BASE_URI !== undefined
    ? process.env.VUE_APP_BASE_URI
    : "https://api.onecoffeeapp.com.au";

// Default display labels for variation types
const DEFAULT_VARIATION_LABELS = {
  size: "Size",
  milk: "Your Choice of Milk",
  sugar: "Sugar",
  strength: "Strength",
  flavour: "Flavour",
};

// Default order for displaying variations
const DEFAULT_VARIATION_ORDER = [
  "size",
  "milk",
  "sugar",
  "strength",
  "flavour",
];

export default defineComponent({
  name: "cafeItemDetailsDrawer",
  data() {
    return {
      quantity: 1,
      instructions: "",
      selectedVariations: {}, // { type: { name, price } }
      totalCartPrice: 0,
      toCartUpdate: false,
      cartOrderId: null,
    };
  },
  components: {
    IonButton,
    IonTextarea,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonTitle,
    IonIcon,
    IonContent,
    IonFooter,
  },
  setup() {
    return {
      chevronBack,
    };
  },
  props: {
    product: Object,
    variations: Object,
    addons: {
      type: Array,
      default: () => [],
    },
    productOriginalPrice: Number,
    productId: Number,
    cartOrders: Object,
    active: Boolean,
    cafeInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    basePrice() {
      return this.productOriginalPrice
        ? parseFloat(this.productOriginalPrice)
        : 0;
    },
    cafeProductId() {
      return this.productId;
    },
    cartList() {
      return this.cartOrders;
    },
    isCafeOpen() {
      return this.cafeInfo?.isOpen === true;
    },
    hasVariations() {
      if (!this.variations) return false;
      return Object.values(this.variations).some(
        (options) => Array.isArray(options) && options.length > 0
      );
    },
    // Returns variation types in preferred order, including any custom types
    variationOrder() {
      if (!this.variations) return [];
      const knownTypes = DEFAULT_VARIATION_ORDER.filter(
        (type) =>
          this.variations[type] &&
          Array.isArray(this.variations[type]) &&
          this.variations[type].length > 0
      );
      // Add any custom variation types not in the default order
      const customTypes = Object.keys(this.variations).filter(
        (type) =>
          !DEFAULT_VARIATION_ORDER.includes(type) &&
          Array.isArray(this.variations[type]) &&
          this.variations[type].length > 0
      );
      return [...knownTypes, ...customTypes];
    },
    hasAllRequiredVariations() {
      // Size is required if it exists
      if (
        this.variations?.size &&
        this.variations.size.length > 0 &&
        !this.selectedVariations.size
      ) {
        return false;
      }
      return true;
    },
    isAddToOrderDisabled() {
      return !this.hasAllRequiredVariations || !this.isCafeOpen;
    },
    // Calculate variations price sum
    variationsTotal() {
      return Object.values(this.selectedVariations).reduce(
        (sum, v) => sum + (v?.price || 0),
        0
      );
    },
  },
  watch: {
    active(newVal) {
      if (newVal) {
        document.body.classList.add("product-drawer-open");
        this.$nextTick(() => {
          this.applyDefaultVariations();
        });
      } else {
        document.body.classList.remove("product-drawer-open");
      }
    },
    variations: {
      handler() {
        if (this.active && !this.toCartUpdate) {
          this.$nextTick(() => {
            this.applyDefaultVariations();
          });
        }
      },
      immediate: true,
      deep: false,
    },
  },
  methods: {
    // Get display label for a variation type
    getVariationLabel(type) {
      // Use default label or title-case the type name for custom types
      // Handles "ice level" -> "Ice Level", "ice_level" -> "Ice Level"
      return (
        DEFAULT_VARIATION_LABELS[type] ||
        type
          .replace(/_/g, " ")
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
      );
    },

    // Generic variation selection handler
    selectVariation(type, option, event) {
      const options = this.variations[type];
      const hasDefault = options?.some((o) => o.isDefault);
      const currentSelection = this.selectedVariations[type];

      // If clicking same option
      if (currentSelection?.name === option.name) {
        // Only allow unselecting if there's no default
        if (!hasDefault) {
          const newSelections = { ...this.selectedVariations };
          delete newSelections[type];
          this.selectedVariations = newSelections;
        }
        // If has default, do nothing (can't unselect)
      } else {
        // Select new option
        this.selectedVariations = {
          ...this.selectedVariations,
          [type]: { name: option.name, price: parseFloat(option.price) || 0 },
        };
      }

      this.recalculatePrice();

      // Force blur to remove focus/active state
      if (event && event.target) {
        event.target.blur();
      }
    },

    // Apply default variations for all types
    applyDefaultVariations() {
      if (this.toCartUpdate || !this.variations) return;

      const newSelections = { ...this.selectedVariations };

      for (const [type, options] of Object.entries(this.variations)) {
        if (!Array.isArray(options) || options.length === 0) continue;
        if (newSelections[type]) continue; // Already selected

        // Find default option, or use first option for 'size' type
        const defaultOption = options.find((o) => o.isDefault);
        if (defaultOption) {
          newSelections[type] = {
            name: defaultOption.name,
            price: parseFloat(defaultOption.price) || 0,
          };
        } else if (type === "size") {
          // Size defaults to first option if no explicit default
          newSelections[type] = {
            name: options[0].name,
            price: parseFloat(options[0].price) || 0,
          };
        }
      }

      this.selectedVariations = newSelections;
      this.recalculatePrice();
    },

    // Recalculate total price
    recalculatePrice() {
      const variationsSum = Object.values(this.selectedVariations).reduce(
        (sum, v) => sum + (v?.price || 0),
        0
      );
      this.totalCartPrice = parseFloat(
        (this.basePrice + variationsSum) * this.quantity
      ).toFixed(2);
    },

    // Build variations array for cart API
    buildVariationsForCart() {
      return Object.entries(this.selectedVariations)
        .filter(([, option]) => option && option.name)
        .map(([type, option]) => ({
          type,
          name: option.name,
          price: option.price,
        }));
    },

    // Compare two variation arrays regardless of order
    variationsEqual(arr1, arr2) {
      if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
      if (arr1.length !== arr2.length) return false;

      // Sort both arrays by type then name for consistent comparison
      const sortFn = (a, b) => {
        const typeCompare = (a.type || "").localeCompare(b.type || "");
        if (typeCompare !== 0) return typeCompare;
        return (a.name || "").localeCompare(b.name || "");
      };

      const sorted1 = [...arr1].sort(sortFn);
      const sorted2 = [...arr2].sort(sortFn);

      return JSON.stringify(sorted1) === JSON.stringify(sorted2);
    },

    async addToCart() {
      const validVariations = this.buildVariationsForCart();

      // Check for existing cart item with same product and variations
      for (let i = 0; i < this.cartList.length; i++) {
        if (
          this.cartList[i].product.id == this.cafeProductId &&
          this.variationsEqual(this.cartList[i].variations, validVariations)
        ) {
          this.quantity = parseInt(this.quantity + this.cartList[i].quantity);
          this.toCartUpdate = true;
          this.cartOrderId = this.cartList[i].uuid;
          this.recalculatePrice();
          break;
        }
      }

      let paramsData = {};
      const axiosConfig = {
        headers: {
          Authorization: localStorage.getItem("token"),
          "Content-Type": "application/json;charset=UTF-8",
        },
      };

      if (!this.toCartUpdate) {
        paramsData = {
          productId: this.cafeProductId,
          variations: validVariations,
          quantity: this.quantity,
          pricePerOrder: this.totalCartPrice,
          instructions: this.instructions,
          userId: localStorage.getItem("id"),
          cafeId: localStorage.getItem("cafeId"),
        };

        await axios
          .post(BASE_URI + "/cart/create-cart-orders", paramsData, axiosConfig)
          .then((res) => {
            if (res.status == 201) {
              this.presentToast("Added to cart!");
              this.$emit("cart-updated");
              this.closeProductOrder();
            }
          })
          .catch((err) => {
            this.handleApiError(err);
            throw err;
          });
      } else {
        paramsData = {
          id: this.cartOrderId,
          variations: validVariations,
          quantity: this.quantity,
          pricePerOrder: this.totalCartPrice,
          instructions: this.instructions,
          status: "pending",
        };

        await axios
          .put(BASE_URI + "/cart/update-cart-orders", paramsData, axiosConfig)
          .then((res) => {
            if (res.status == 200) {
              this.presentToast("Cart updated!");
              this.$emit("cart-updated");
              this.closeProductOrder();
            }
          })
          .catch((err) => {
            this.handleApiError(err);
            throw err;
          });

        this.toCartUpdate = false;
        this.cartOrderId = null;
      }

      this.closeProductOrder();
    },

    handleApiError(err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        this.presentToast("Sign in to continue.", "warning");
        setTimeout(() => {
          window.location.href = window.location.origin + "/welcome";
        }, 1000);
      } else if (err.response?.status === 400) {
        this.presentToast(
          err.response.data?.error ||
            "Invalid request. Please check your selections.",
          "danger"
        );
      } else if (err.response?.status >= 500) {
        this.presentToast("Server error. Please try again later.", "danger");
      } else if (!err.response) {
        this.presentToast(
          "Network error. Please check your connection.",
          "danger"
        );
      } else {
        this.presentToast("An error occurred. Please try again.", "danger");
      }
    },

    closeProductOrder() {
      this.selectedVariations = {};
      this.quantity = 1;
      this.totalCartPrice = 0;
      this.instructions = "";
      this.$emit("close-product-drawer");
    },

    increment() {
      this.quantity++;
      this.recalculatePrice();
    },

    decrement() {
      if (this.quantity > 1) {
        this.quantity--;
        this.recalculatePrice();
      }
    },

    async presentToast(message, type = "success") {
      const cssClass =
        type === "success" ? "custom-toast" : `custom-toast toast-${type}`;

      const toast = await toastController.create({
        message: message,
        duration: 2000,
        position: "top",
        cssClass: cssClass,
      });

      await toast.present();
    },
  },
  beforeMount() {
    this.$nextTick(() => {
      this.totalCartPrice =
        this.totalCartPrice == 0
          ? this.basePrice
          : parseFloat(this.totalCartPrice).toFixed(2);
    });
  },
  beforeUnmount() {
    document.body.classList.remove("product-drawer-open");
  },
});
</script>
<style lang="scss" scoped>
.product-details {
  margin-bottom: 0;
  margin-top: 0;
  height: auto;
  padding-top: 10px;
  padding-bottom: 20px;
}
.row-addon {
  margin-bottom: 20px;
}
.hrdivider {
  border-bottom: 1px solid #f3f3f3;
}
.product-name {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  color: #f87e63;
  padding-left: 15px;
  padding-bottom: 0px;
  margin-top: 5px;
}
.product-quantity-heading {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  color: #1b1b1b;
  padding-left: 20px;
  margin-bottom: 5px;
  margin-top: -10px;
}
.product-addon {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  color: #1b1b1b;
  padding-left: 20px;
  margin-bottom: 5px;
  margin-top: 5px;
}
.product-addon-fr {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  color: #1b1b1b;
  margin-right: 20px;
  margin-bottom: 5px;
  margin-top: 5px;
  float: right;
}
.product-addon-name {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  color: #666666;
}
.product-addon-price {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  color: #1b1b1b;
  position: absolute;
  right: 16px;
}
.product-txtarea {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 135%;
  color: #1b1b1b;
  padding-left: 20px;
}
ion-button {
  --color: #1b1b1b;
  --border-radius: 15px;
  --border-style: solid;
  --border-width: 1px;
  --border-color: #f87e63;
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  margin-top: 0px;
  margin-bottom: 10px;
  margin-left: 15px;
  margin-right: 15px;
  height: 52px;
  text-transform: none;
  -webkit-border-radius: 15px;
  -moz-border-radius: 15px;
  border-radius: 15px;
  -webkit-box-shadow: rgba(0, 0, 0, 0.8) 0px 0 10px;
  -moz-box-shadow: rgba(0, 0, 0, 0.8) 0 0 10px;
  box-shadow: rgba(0, 0, 0, 0.8) 0 0 10px;
}
ion-button:active {
  --color: #f6b8b8;
}
.carttext-btn {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
}
.cartprice-btn {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  position: absolute;
  right: 16px;
}
.drawer-background {
  width: 100%;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  background-color: rgba(124, 124, 124, 0.55);
  z-index: 1000;
  display: none;
  transition: display 0.5s;
  &.show {
    display: block;
  }
}
.drawer {
  width: 100vw;
  height: 100vh;
  background-color: white;
  position: fixed;
  top: 0;
  left: 105vw;
  padding: 0px;
  transition: left 0.5s;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  &.show {
    left: 0;
  }
}

.drawer ion-header {
  flex-shrink: 0;
}

.drawer ion-content {
  flex: 1;
  overflow-y: auto;
  --background: #ffffff;
}

.drawer ion-footer {
  flex-shrink: 0;
  background: transparent;
}
.product-quantity {
  margin-left: auto;
  margin-right: auto;
  margin-top: 5px;
  margin-bottom: 5px;
  width: 40%;
}
@media only screen and (min-width: 768px) and (max-width: 834px) {
  .product-quantity {
    margin-left: auto;
    margin-right: auto;
    margin-top: 5px;
    margin-bottom: 5px;
    width: 20%;
  }
}
@media only screen and (min-width: 1024px) and (max-width: 1024px) and (orientation: landscape) {
  .product-quantity {
    margin-left: auto;
    margin-right: auto;
    margin-top: 5px;
    margin-bottom: 5px;
    width: 17%;
  }
}
@media only screen and (min-width: 820px) and (max-width: 1180px) and (orientation: portrait) {
  .product-quantity {
    margin-left: auto;
    margin-right: auto;
    margin-top: 5px;
    margin-bottom: 5px;
    width: 20%;
  }
}
@media only screen and (min-width: 1180px) and (max-width: 1194px) and (orientation: landscape) {
  .product-quantity {
    margin-left: auto;
    margin-right: auto;
    margin-top: 5px;
    margin-bottom: 5px;
    width: 22%;
  }
}
@media only screen and (min-width: 1024px) and (max-width: 1366px) and (orientation: portrait) {
  .product-quantity {
    margin-left: auto;
    margin-right: auto;
    margin-top: 5px;
    margin-bottom: 5px;
    width: 16%;
  }
}
@media only screen and (min-width: 1366px) and (max-width: 1366px) and (min-height: 1024px) and (max-height: 1024px) and (orientation: landscape) {
  .product-quantity {
    margin-left: auto;
    margin-right: auto;
    margin-top: 5px;
    margin-bottom: 5px;
    width: 20%;
  }
}
@media (min-width: 500px) {
  .drawer {
    width: 100%;
  }
}
ion-header {
  box-shadow: none;
}
ion-toolbar {
  --background: #f87e63;
  --color: #ffffff;
  --border-width: 0;
  --padding-top: 8px;
  --padding-bottom: 8px;
  --padding-start: 16px;
  --padding-end: 16px;
  --min-height: 64px;
}
ion-title {
  font-family: "Gilroy", sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  color: #ffffff;
  text-align: center;
}
ion-buttons ion-button {
  --padding-start: 0;
  --padding-end: 0;
  margin: 0;
  --box-shadow: none;
  box-shadow: none;
}
ion-buttons ion-icon {
  color: #ffffff;
  font-size: 24px;
}

/* Footer Styles */
.drawer ion-footer {
  box-shadow: none !important;
  border: none !important;
  background: transparent !important;
  --background: transparent !important;
}

.drawer ion-footer ion-toolbar {
  --background: transparent !important;
  --border-width: 0 !important;
  --padding-top: 10px;
  --padding-bottom: 10px;
  --padding-start: 15px;
  --padding-end: 15px;
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}

/* New Grid Styles */
.product-name-centered {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  color: #f87e63;
  text-align: center;
  padding: 20px 15px;
  margin: 0;
}

/* Quantity Selector */
.quantity-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 8px auto 20px auto;
}

.qty-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: #f3f3f3;
  color: #737373;
  font-size: 22px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.qty-btn:active:not(:disabled) {
  transform: scale(0.95);
  background: #e8e8e8;
}

.qty-btn:disabled {
  background: #f3f3f3;
  color: #c0c0c0;
  cursor: not-allowed;
}

.qty-icon {
  line-height: 1;
  font-family: "Gilroy", system-ui, sans-serif;
}

.qty-display {
  min-width: 80px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f87e63;
  border-radius: 22px;
}

.qty-value {
  font-family: "Gilroy", system-ui, sans-serif;
  font-weight: 600;
  font-size: 18px;
  color: #ffffff;
}

.variations-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 10px 20px 20px 20px;
}

.variation-label {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 10px;
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  background: #f3f3f3;
  color: #737373;
  border-radius: 15px;
  border: 1px solid #f3f3f3;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  min-height: 60px;
  justify-content: center;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

/* Only apply hover on non-touch devices */
@media (hover: hover) and (pointer: fine) {
  .variation-label:hover {
    background-color: #f87e63;
    color: #ffffff;
  }
}

.variation-label:focus {
  outline: 2px solid #f87e63;
  outline-offset: 2px;
}

.variation-label:active {
  outline: none;
}

.variation-label:not(.selected):focus {
  background: #f3f3f3;
  color: #737373;
}

.variation-label:not(.selected):active {
  background: #e8e8e8;
  color: #737373;
}

.variation-label.selected {
  background-color: #f87e63;
  border-color: #f87e63;
  color: #ffffff;
}

.addon-price-label {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 140%;
  color: inherit;
  display: block;
  margin-top: 4px;
}
</style>

<style>
/* Global styles for hiding tab bar and footer when drawer is open */
body.product-drawer-open ion-tab-bar {
  display: none !important;
}

body.product-drawer-open ion-footer:not(.drawer ion-footer) {
  display: none !important;
}

/* Toast notification styles */
.custom-toast {
  --background: #ffffff;
  --color: #1b1b1b;
  --border-radius: 10px;
  --box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  font-family: "Gilroy", sans-serif;
  font-weight: 500;
  font-size: 14px;
}

.custom-toast.toast-danger {
  --background: #ffe0e3;
  --color: #1b1b1b;
}

.custom-toast.toast-warning {
  --background: #fff0d9;
  --color: #1b1b1b;
}
</style>
